/**
 * IRS names arrive in capitals. Capitals are slower to read and flatten the
 * difference between a name and the purpose line beneath it, so the site
 * shows a cased form and keeps the original in a `title` attribute. The
 * transform is conservative on purpose: a token the rules are unsure of is
 * left as written, because "Ymca" is worse than "YMCA" and "INC" is only
 * slightly worse than "Inc". A name that already carries lowercase letters
 * is not touched at all.
 */
const KEEP = new Set([
  'LLC', 'LLP', 'USA', 'US', 'UK', 'DC', 'NYC', 'YMCA', 'YWCA', 'AIDS', 'HIV', 'NAACP', 'ACLU', 'PTA', 'PTO', 'FBO', 'DBA', 'UW', 'TR', 'TTEE', 'TTEES',
  'II', 'III', 'IV', 'MIT', 'UCLA', 'USC', 'NYU', 'CUNY', 'SUNY', 'AARP', 'UNICEF', 'PBS', 'NPR', 'SPCA', 'ASPCA', 'FFA', 'ROTC', 'VFW', 'AME', 'UMC',
  'ELCA', 'LDS', 'JCC', 'KIPP', 'UJA', 'JCRC', 'NA', 'AA', 'CPA', 'MD', 'DDS', 'PC', 'PA',
  // Unions and civic bodies whose initials happen to contain vowels.
  'IBEW', 'SEIU', 'UAW', 'AFSCME', 'AFL', 'CIO', 'CWA', 'UFCW', 'IATSE', 'LGBTQ', 'LGBT', 'UNCF', 'USO', 'NRA', 'BSA', 'GSA', 'ADL', 'AJC', 'AAUW', 'NEA', 'AFT',
]);
const SMALL = new Set(['OF', 'THE', 'AND', 'FOR', 'IN', 'ON', 'AT', 'TO', 'BY', 'A', 'AN', 'DE', 'DU', 'DES', 'VAN', 'VON', 'DEL', 'DA', 'DI']);
const SPECIAL: Readonly<Record<string, string>> = { ST: 'St', MT: 'Mt', FT: 'Ft', DR: 'Dr', MR: 'Mr', MRS: 'Mrs', MS: 'Ms', JR: 'Jr', SR: 'Sr' };

/**
 * Lowercase the word and raise its first letter, wherever that letter is: "(THE)" becomes "(The)".
 * Unicode letter classes rather than A-Z throughout this module: a Schedule I name can carry
 * accented capitals, and an ASCII class would skip the "É" of "ÉCOLE" and raise the "c" instead.
 */
const capitalize = (word: string): string => word.toLowerCase().replace(/\p{Ll}/u, (letter) => letter.toUpperCase());

const mc = (word: string): string => (word.length > 3 && word.startsWith('Mc') ? `Mc${capitalize(word.slice(2))}` : word);

const casePiece = (piece: string, afterApostrophe: boolean): string => (afterApostrophe && piece.length <= 1 ? piece.toLowerCase() : mc(capitalize(piece)));

const letters = (word: string): string => word.replace(/[^\p{L}]/gu, '');

const caseWord = (raw: string, first: boolean): string => {
  const core = raw.replace(/[^\p{L}\p{N}]/gu, '');
  if (core.length === 0 || /\d/.test(raw) || raw.includes('&') || KEEP.has(core)) {
    return raw;
  }
  const parts = raw.split('-');
  if (parts.length > 1) {
    // "AFL-CIO", "CO-OP": short parts joined by hyphens are initials, kept as written. Longer parts
    // are words, each cased on its own so "UW-MADISON" keeps its kept token.
    return parts.every((part) => letters(part).length <= 3) ? raw : parts.map((part) => caseWord(part, first)).join('-');
  }
  const special = SPECIAL[core];
  if (special !== undefined) {
    return raw.replace(core, special);
  }
  if (SMALL.has(core)) {
    return first ? capitalize(raw) : raw.toLowerCase();
  }
  if (core.length <= 2) {
    return raw;
  }
  // Three or more letters with no vowel is an abbreviation, not a word: "LGBTQ", "DVM". Y counts as a
  // vowel so "GYM" is still cased.
  if (!/[AEIOUY]/u.test(letters(raw))) {
    return raw;
  }
  return raw
    .split("'")
    .map((piece, index) => casePiece(piece, index > 0))
    .join("'");
};

export const displayName = (name: string | null): string | null => {
  if (name === null || name.length === 0 || /\p{Ll}/u.test(name)) {
    return name;
  }
  return name
    .split(' ')
    .map((word, index) => caseWord(word, index === 0))
    .join(' ');
};
